/**
 * Инструменты подложки на холсте: масштаб по известной стене и сдвиг картинки.
 *
 *   • Масштаб (`Tool.UnderlayScale`): клик в начало стены, клик в конец — открывается окошко
 *     с её длиной; по ней подложка растягивается от первой точки (`calibrate`). Пока ведёте
 *     ко второй точке, за курсором тянется отрезок с текущей длиной.
 *   • Сдвиг (`Tool.UnderlayMove`): тянете в любом месте — едет вся картинка.
 *
 * Клики ловим на погружении (`handlePointerDownCapture`): иначе их забрали бы стены и мебель
 * поверх подложки. Только левая кнопка — средняя и правая по-прежнему двигают вид.
 * Правка подложки отменяется Ctrl+Z, как любая правка проекта.
 */
import { useRef, useState } from "react";
import type { PointerEvent } from "react";
import type { Pt } from "@/shared/lib/geometry";
import { useProjectStore } from "@/shared/store";
import { useUnderlayStore } from "@/shared/store/layers";
import { Tool, useEditorStore } from "@/widgets/editor";
import { useUnderlayRecognitionStore } from "../recognitionStore";

/** Отрезок, по которому задают масштаб, — ждёт настоящую длину. */
export interface UnderlayMeasure {
	from: Pt;
	to: Pt;
}

interface Params {
	toWorld: (clientX: number, clientY: number) => Pt;
}

export const useUnderlayTools = ({ toWorld }: Params) => {
	const tool = useEditorStore((s) => s.tool);
	/** Первая точка отрезка масштаба; null — ещё не кликнули */
	const [scaleFrom, setScaleFrom] = useState<Pt | null>(null);
	/** Где сейчас курсор — второй конец отрезка, пока его ведут */
	const [preview, setPreview] = useState<Pt | null>(null);
	/** Отрезок готов — открыто окошко длины */
	const [measure, setMeasure] = useState<UnderlayMeasure | null>(null);
	/**
	 * Сдвиг: где схватили и где был угол картинки. История пишется при первом сдвиге,
	 * а не при нажатии — простой клик не оставляет в Ctrl+Z пустой шаг
	 */
	const drag = useRef<{ grabbed: Pt; origin: Pt; recorded: boolean } | null>(null);

	const scaling = tool === Tool.UnderlayScale;
	const moving = tool === Tool.UnderlayMove;

	// Сменили инструмент или нажали Esc — недоделанный отрезок забывается (сброс при рендере,
	// а не в эффекте: так React не рисует лишний кадр со старым отрезком). Сдвиг кончается сам —
	// без своего инструмента его движение не обрабатывается
	const [seenTool, setSeenTool] = useState(tool);
	if (seenTool !== tool) {
		setSeenTool(tool);
		setScaleFrom(null);
		setPreview(null);
	}

	/** Клик на погружении: true — клик забран инструментом подложки. */
	const handlePointerDownCapture = (e: PointerEvent<SVGSVGElement>): boolean => {
		if ((!scaling && !moving) || e.button !== 0) return false;
		if (e.pointerType === "touch" && !e.isPrimary) return false;
		e.stopPropagation();
		const world = toWorld(e.clientX, e.clientY);
		if (scaling) {
			if (!scaleFrom) {
				setScaleFrom(world);
				setPreview(world);
			} else if (Math.hypot(world.x - scaleFrom.x, world.y - scaleFrom.y) >= 1) {
				setMeasure({ from: scaleFrom, to: world });
				setScaleFrom(null);
				setPreview(null);
			}
			return true;
		}
		const underlay = useUnderlayStore.getState();
		if (!underlay.image) return true;
		drag.current = { grabbed: world, origin: { x: underlay.x, y: underlay.y }, recorded: false };
		e.currentTarget.setPointerCapture(e.pointerId);
		return true;
	};

	const handlePointerMove = (e: PointerEvent<SVGSVGElement>) => {
		if (scaling && scaleFrom) {
			setPreview(toWorld(e.clientX, e.clientY));
			return;
		}
		const state = drag.current;
		if (!moving || !state) return;
		if (!state.recorded) {
			useProjectStore.getState().pushHistory();
			useUnderlayRecognitionStore.getState().clear();
			state.recorded = true;
		}
		const world = toWorld(e.clientX, e.clientY);
		useUnderlayStore.getState().moveTo({
			x: state.origin.x + world.x - state.grabbed.x,
			y: state.origin.y + world.y - state.grabbed.y,
		});
	};

	const handlePointerUp = () => {
		drag.current = null;
	};

	/** Ввели настоящую длину отрезка, мм: задаём масштаб, инструмент — снова «Выбор». */
	const finishScale = (length: number) => {
		if (!measure) return;
		useProjectStore.getState().pushHistory();
		useUnderlayStore.getState().calibrate(measure.from, measure.to, length);
		useUnderlayRecognitionStore.getState().clear();
		setMeasure(null);
		useEditorStore.getState().setTool(Tool.Select);
	};

	/** Закрыли окошко без длины — можно отметить отрезок заново. */
	const cancelScale = () => setMeasure(null);

	return {
		/** Отрезок масштаба, который сейчас ведут: от первой точки к курсору */
		scaleLine: scaleFrom && preview ? { from: scaleFrom, to: preview } : null,
		measure,
		moving,
		finishScale,
		cancelScale,
		handlePointerDownCapture,
		handlePointerMove,
		handlePointerUp,
	};
};
